import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import StarIcon from '@mui/icons-material/Star';
import { useNavigate } from 'react-router-dom';
import PlanEditor from '../../article/PlanEditor';
import { getLLMModels } from '../../../services/api';
import { useProject } from '../../../contexts/ProjectContext';

const PlanStep = ({ data, onDataUpdate, onGeneratePlan }) => {
  const navigate = useNavigate();
  const { selectedProject } = useProject();
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  const isShortPost = data.article?.type === 'short_social_post';

  useEffect(() => {
    const loadModels = async () => {
      try {
        const response = await getLLMModels();
        setModels(response);
        // Set default model to gpt-4o-mini or first available
        const defaultModel = response.find(m => m.name === 'gpt-4o-mini') || response[0];
        setSelectedModel(defaultModel?.name);
      } catch (error) {
        console.error('Error loading models:', error);
      } finally {
        setModelsLoading(false);
      }
    };

    loadModels();
  }, [selectedProject]);

  const handlePlanChange = (newPlan) => {
    onDataUpdate('plan', newPlan);
  };

  const handleGeneratePlan = async () => {
    setIsGenerating(true);
    try {
      await onGeneratePlan(selectedModel);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Box sx={{ 
      width: '100%',
      '& .MuiCard-root': {
        boxShadow: { xs: 'none', sm: 1 }
      },
      position: 'relative',
      opacity: isGenerating ? 0.6 : 1,
      transition: 'opacity 0.2s ease-in-out'
    }}>
      {isGenerating && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 10,
            borderRadius: 1
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Crafting your article plan...
            </Typography>
          </Box>
        </Box>
      )}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 1, sm: 2 },
        mb: 2 
      }}>
        <Typography variant="h6" sx={{ mb: { xs: 0.5, sm: 0 } }}>
          Article Plan
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'stretch',
          gap: 1,
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%'
          }}>
            <FormControl 
              size="small" 
              sx={{ 
                flex: 1,
                '& .MuiInputBase-root': {
                  height: '32px'
                }
              }}
            >
              <InputLabel>Model</InputLabel>
              <Select
                value={selectedModel || ''}
                label="Model"
                onChange={(e) => setSelectedModel(e.target.value)}
                disabled={modelsLoading || isGenerating}
                sx={{
                  opacity: modelsLoading ? 0.7 : 1
                }}
                startAdornment={
                  modelsLoading ? (
                    <CircularProgress size={16} sx={{ ml: 1, mr: 1 }} />
                  ) : null
                }
                endAdornment={
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <Tooltip title="Set default model in project settings">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/settings?highlight=default_paragraph_planner_model`);
                        }}
                        sx={{ 
                          color: 'primary.main',
                          p: 0.5
                        }}
                      >
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: '300px'
                    }
                  }
                }}
              >
                {models.map((model) => (
                  <MenuItem 
                    key={model.name} 
                    value={model.name}
                    sx={{ 
                      py: 1,
                      minHeight: '32px'
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      fontSize: '0.875rem'
                    }}>
                      <span>{model.name} ({model.provider})</span>
                      {model.name === selectedProject?.settings?.default_paragraph_planner_model && (
                        <Typography variant="caption" color="primary.main" sx={{ fontStyle: 'italic' }}>
                          (Default)
                        </Typography>
                      )}
                      {model.premium && (
                        <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="contained"
            onClick={handleGeneratePlan}
            disabled={isGenerating || modelsLoading}
            size="small"
            startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ 
              flex: 1,
              height: '32px',
              whiteSpace: 'nowrap',
              position: 'relative'
            }}
          >
            {isGenerating ? 'Generating...' : data.plan ? 'Regenerate Plan' : 'Generate Plan'}
          </Button>
        </Box>
      </Box>

      <PlanEditor
        rawPlan={data.plan}
        onChange={handlePlanChange}
        onGeneratePlan={handleGeneratePlan}
        isGenerating={isGenerating}
        disabled={isGenerating}
        isShortPost={isShortPost}
      />
    </Box>
  );
};

export default PlanStep; 